// utils/SoundManager.js
import { HowlCache } from "./State/Slices/soundsSlice"; // ← use the Redux cache
import { SOUND_NAME } from "./sounds";

class SoundManager {
  constructor() {
    // initial local mirrors; Redux will toggle these via reducers later
    this.soundEffectsEnabled = true;
    this.backgroundMusicEnabled = false;
    this.globalSoundDisabled = false;
    this.masterVolume = 1.0;
    this.currentBackgroundMusic = SOUND_NAME.background_music;
  }

  /**
   * Play a sound from the shared HowlCache
   * Handles both individual sounds and audio sprite sounds
   */
  play(name) {
    if (this.globalSoundDisabled) return;

    // Check if this sound is in the sprite
    const spriteSound = HowlCache['sfx_sprite'];
    if (spriteSound && spriteSound._sprite && spriteSound._sprite[name]) {
      // It's a sprite sound - play the specific sprite
      // Drop sounds require background music to be enabled
      if (this._isDropSound(name)) {
        if (this.soundEffectsEnabled && this.backgroundMusicEnabled) {
          spriteSound.play(name);
        }
      } else if (this.soundEffectsEnabled) {
        spriteSound.play(name);
      }
      return;
    }

    // Not a sprite sound - play as individual sound (looping sounds)
    const sound = HowlCache[name];
    if (!sound) {
      console.warn(`[SoundManager] Missing sound: ${name}`);
      return;
    }

    if (this._isMusic(name)) {
      if (this.backgroundMusicEnabled) {
        sound.mute(false);
        sound.play();

        // If playing background music or feature music, also start ambient music
        if (name === SOUND_NAME.background_music || name === SOUND_NAME.feature_background_music) {
          const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
          if (ambientMusic && !ambientMusic.playing()) {
            ambientMusic.mute(false);
            ambientMusic.play();
          }
        }
      }
    } else if (this.soundEffectsEnabled) {
      sound.mute(false);
      sound.play();
    }
  }

  stop(name) {
    const sound = HowlCache[name];
    if (sound && sound.playing()) {
      sound.stop();

      // If stopping background music or feature music, also stop ambient music
      if (name === SOUND_NAME.background_music || name === SOUND_NAME.feature_background_music) {
        const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
        if (ambientMusic && ambientMusic.playing()) {
          ambientMusic.stop();
        }
      }
    }
  }

  /**
   * Fade out a sound gradually over the specified duration
   * @param {string} name - Sound name to fade out
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeOut(name, duration = 1000, onComplete = null) {
    const sound = HowlCache[name];
    if (!sound) {
      console.warn(`[SoundManager] Missing sound for fadeOut: ${name}`);
      if (onComplete) onComplete();
      return;
    }

    if (!sound.playing()) {
      if (onComplete) onComplete();
      return;
    }

    const currentVolume = sound.volume();
    sound.fade(currentVolume, 0, duration);

    // If fading out background music or feature music, also fade out ambient music
    const shouldFadeAmbient = name === SOUND_NAME.background_music || name === SOUND_NAME.feature_background_music;
    if (shouldFadeAmbient) {
      const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
      if (ambientMusic && ambientMusic.playing()) {
        const ambientVolume = ambientMusic.volume();
        ambientMusic.fade(ambientVolume, 0, duration);
        setTimeout(() => {
          ambientMusic.stop();
          ambientMusic.volume(ambientVolume);
        }, duration);
      }
    }

    // Stop the sound after fade completes
    setTimeout(() => {
      sound.stop();
      // Reset volume to original for next play
      sound.volume(currentVolume);
      if (onComplete) onComplete();
    }, duration);
  }

  /**
   * Fade in a sound gradually over the specified duration
   * @param {string} name - Sound name to fade in
   * @param {number} duration - Duration in milliseconds (default: 1000ms)
   * @param {number} targetVolume - Target volume (default: 1.0)
   * @param {Function} onComplete - Optional callback when fade completes
   */
  fadeIn(name, duration = 1000, targetVolume = 1.0, onComplete = null) {
    const sound = HowlCache[name];
    if (!sound) {
      console.warn(`[SoundManager] Missing sound for fadeIn: ${name}`);
      if (onComplete) onComplete();
      return;
    }

    if (this.globalSoundDisabled) {
      if (onComplete) onComplete();
      return;
    }

    // Check if it's music and if music is enabled
    if (this._isMusic(name)) {
      if (!this.backgroundMusicEnabled) {
        if (onComplete) onComplete();
        return;
      }
    } else {
      if (!this.soundEffectsEnabled) {
        if (onComplete) onComplete();
        return;
      }
    }

    // Set initial volume to 0 and start playing
    sound.volume(0);
    sound.mute(false);
    sound.play();

    // Fade in to target volume
    sound.fade(0, targetVolume, duration);

    // If fading in background music or feature music, also fade in ambient music
    if (name === SOUND_NAME.background_music || name === SOUND_NAME.feature_background_music) {
      const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
      if (ambientMusic && !ambientMusic.playing()) {
        ambientMusic.volume(0);
        ambientMusic.mute(false);
        ambientMusic.play();
        ambientMusic.fade(0, targetVolume, duration);
      }
    }

    // Call onComplete after fade completes
    if (onComplete) {
      setTimeout(() => {
        onComplete();
      }, duration);
    }
  }

  toggleGlobal(disabled) {
    this.globalSoundDisabled = disabled;
    Object.values(HowlCache).forEach((sound) => {
      sound.mute(disabled);
      if (disabled) sound.stop();
    });
    this.soundEffectsEnabled = !disabled;
    this.backgroundMusicEnabled = !disabled;

    // If re-enabling, start ambient music if background music is enabled
    if (!disabled && this.backgroundMusicEnabled) {
      const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
      if (ambientMusic) {
        ambientMusic.mute(false);
        ambientMusic.play();
      }
    }
  }

  toggleBackgroundMusic(enabled) {
    this.backgroundMusicEnabled = enabled;
    const current = HowlCache[this.currentBackgroundMusic];
    const ambientMusic = HowlCache[SOUND_NAME.ambient_music];

    if (enabled && !this.globalSoundDisabled) {
      if (current) {
        current.mute(false);
        current.play();
      }
      // Play ambient music alongside background music
      if (ambientMusic) {
        ambientMusic.mute(false);
        ambientMusic.play();
      }
    } else {
      if (current) {
        current.stop();
      }
      // Stop ambient music when background music stops
      if (ambientMusic) {
        ambientMusic.stop();
      }
    }
  }

  /**
   * Switch to a different background music track
   * @param {string} musicName - The music track to switch to
   */
  switchBackgroundMusic(musicName) {
    const wasPlaying = this.backgroundMusicEnabled && !this.globalSoundDisabled;

    // Stop current music if playing
    if (this.currentBackgroundMusic) {
      const currentMusic = HowlCache[this.currentBackgroundMusic];
      if (currentMusic && currentMusic.playing()) {
        currentMusic.stop();
      }
    }

    // Update current music reference
    this.currentBackgroundMusic = musicName;

    // Play new music if music was enabled
    if (wasPlaying) {
      const newMusic = HowlCache[musicName];
      if (newMusic) {
        newMusic.mute(false);
        newMusic.play();
      }
    }
  }

  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(2, volume));
    Object.values(HowlCache).forEach((sound) => {
      sound.volume(this.masterVolume);
    });
  }

  _isMusic(name) {
    return [
      SOUND_NAME.background_music,
      SOUND_NAME.feature_background_music,
      SOUND_NAME.ambient_music,
    ].includes(name);
  }

  _isDropSound(name) {
    return [
      SOUND_NAME.drop_1,
      SOUND_NAME.drop_2,
      SOUND_NAME.drop_3,
      SOUND_NAME.drop_4,
      SOUND_NAME.drop_5,
      SOUND_NAME.drop_6,
      SOUND_NAME.drop_7,
      SOUND_NAME.drop_8,
      SOUND_NAME.drop_9,
      SOUND_NAME.drop_10,
      SOUND_NAME.drop_11,
      SOUND_NAME.drop_12,
    ].includes(name);
  }
}

export const soundManager = new SoundManager();
